import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useKnowledgeBasesList } from '@/shared/hooks/useKnowledgeBasesList';
import type {
  KnowledgeBaseImageFile,
  KnowledgeBaseImagesResult,
  KnowledgeBaseListItem,
} from '@/shared/ui/ImageSourcePicker';
import { getFilesByKnowledgeBaseId } from '../../api/knowledgeBases.api';

/**
 * Bundles kb-listing + kb-image-files into the shape the shared `<ImageSourcePicker>` expects
 * (`UseKnowledgeBaseImagesHook`). Lives in the knowledge-bases feature (it value-imports
 * `getFilesByKnowledgeBaseId`) and is injected as a prop into consumers (the shared
 * `ImageEditDialog` used by templates-config and the email widget) so those slices stay free of
 * cross-feature value imports.
 */
export function useKnowledgeBaseImages(selectedKbId: string | null): KnowledgeBaseImagesResult {
  const { data: knowledgeBases = [] } = useKnowledgeBasesList();

  const selectableKBs: KnowledgeBaseListItem[] = useMemo(
    () =>
      knowledgeBases
        .filter(
          (kb): kb is typeof kb & { id: string; name: string } =>
            !!kb.id && !!kb.name && !kb.isInternal,
        )
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((kb) => ({ id: kb.id, name: kb.name })),
    [knowledgeBases],
  );

  const { data: kbFiles = [], isLoading: filesLoading } = useQuery({
    queryKey: ['knowledgeBaseImages', selectedKbId],
    queryFn: () => getFilesByKnowledgeBaseId(selectedKbId!, {}),
    enabled: !!selectedKbId,
    staleTime: 5 * 60 * 1000,
  });

  const imageFiles: KnowledgeBaseImageFile[] = useMemo(
    () =>
      kbFiles
        .filter((f) => f.mimeType.startsWith('image/') && f.assetId)
        .map((f) => ({
          fileId: f.id,
          fileName: f.fileName,
          mimeType: f.mimeType,
          assetId: f.assetId as string,
        })),
    [kbFiles],
  );

  return {
    knowledgeBases: selectableKBs,
    imageFiles,
    filesLoading,
  };
}
